import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Switch, Typography } from '@mui/material';
import { fetchProductSalesChannels, setProductSalesChannel } from '../api/inventoryAdmin';
import type { ProductSalesChannel, ProductSalesChannelsState } from '../api/inventoryAdmin';

interface ProductSalesChannelsPanelProps {
  inventoryId: number;
}

function ProductSalesChannelsPanel({
  inventoryId,
}: ProductSalesChannelsPanelProps): React.ReactElement | null {
  const [state, setState] = useState<ProductSalesChannelsState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await fetchProductSalesChannels(inventoryId);
    if (!result.ok) {
      setError(result.error);
      setState(null);
      setLoading(false);
      return;
    }
    setState(result.data);
    setLoading(false);
  }, [inventoryId]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const onToggle = async (channel: ProductSalesChannel, next: boolean) => {
    if (!state) return;
    if (next && !state.canEnable) return;
    const previous = state;
    setPendingId(channel.id);
    setError(null);
    setState({
      ...state,
      channels: state.channels.map((c) => (c.id === channel.id ? { ...c, published: next } : c)),
    });
    const result = await setProductSalesChannel(inventoryId, channel.id, next);
    setPendingId(null);
    if (!result.ok) {
      setState(previous);
      setError(result.error);
      return;
    }
    setState(result.data);
  };

  if (loading) {
    return (
      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
        }}
      >
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Cargando canales de venta…
        </Typography>
      </Box>
    );
  }

  if (error && !state) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!state?.posted) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Canales de venta
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {state.canEnable
          ? 'Activa o desactiva en qué canales de Shopify aparece este producto.'
          : 'Agrega al menos una foto para poder activar canales. Sí puedes desactivar los que ya están publicados.'}
      </Typography>
      {error ? (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      ) : null}
      <Box sx={{ mt: 1.25 }}>
        {state.channels.map((channel, index) => {
          const disableOn = !state.canEnable && !channel.published;
          return (
            <Box
              key={channel.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                py: 1,
                borderTop: index === 0 ? '1px solid' : undefined,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {channel.name}
              </Typography>
              <Switch
                checked={channel.published}
                disabled={pendingId === channel.id || disableOn}
                onChange={(_, checked) => {
                  onToggle(channel, checked).catch(() => {});
                }}
                size="small"
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default ProductSalesChannelsPanel;
